import {
  fetchWithErrorHandling,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {threadPreviewsQueryKey} from '../page-data/loaders/use-thread-previews'
import {useMarkersActions, usePullRequestBasePath, useUnresolvedConversationCountActions} from '../stores/selectors'
import type {FileKey, LineKey} from '@github-ui/diff-lines/types'
import {pullRequestReopenAutomatedReviewCommentPath} from '@github-ui/paths'

export function useReopenAutomatedReviewThreadMutation(filePath: string, lineMarkersKey: LineKey | FileKey) {
  const basePath = usePullRequestBasePath()
  const queryClient = useQueryClient()
  const {updateThread} = useMarkersActions()
  const {incrementUnresolvedConversationCount} = useUnresolvedConversationCountActions()

  return useMutation({
    mutationFn: async ({automatedReviewCommentId}: {automatedReviewCommentId: string; threadId: string}) => {
      const apiUrl = pullRequestReopenAutomatedReviewCommentPath({basePath, automatedReviewCommentId})
      const response = await fetchWithErrorHandling(apiUrl, {method: 'PUT'})

      throwErrorsIfBadResponse(response)
    },
    onSuccess: (_data, variables) => {
      let wasResolved = false
      updateThread(Number(variables.threadId), filePath, lineMarkersKey, oldThread => {
        if (oldThread?.commentsData?.comments[0]?.automatedComment) {
          const automatedComment = oldThread.commentsData?.comments[0]?.automatedComment
          wasResolved = oldThread.isResolved ?? false
          return {
            ...oldThread,
            isResolved: false,
            resolvedBy: undefined,
            commentsData: {
              ...oldThread.commentsData,
              comments: [
                {
                  ...oldThread.commentsData.comments[0],
                  automatedComment: {
                    ...automatedComment,
                    isDismissed: false,
                    viewerCanDismiss: true,
                    viewerCanReopen: false,
                  },
                },
                ...oldThread.commentsData.comments.slice(1),
              ],
            },
          }
        }
      })
      if (wasResolved) incrementUnresolvedConversationCount()

      // invalidate pullRequestFilesToolbar queries
      queryClient.invalidateQueries({
        queryKey: threadPreviewsQueryKey(basePath),
      })
    },
  })
}
